import { useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Skeleton,
  IconButton,
  TableContainer,
} from '@mui/material';
import { Edit } from '@mui/icons-material';

import { WhatsappTemplateWithEnabled } from '@bandido/shared/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { WhatsappTemplateConfigDialog } from '@frontend/features/whatsapp/components/WhatsappTemplateConfigDialog';
import { useGetTemplates } from '@frontend/features/whatsapp/hooks/useGetTemplates';
import { useUpdateTemplateConfig } from '@frontend/features/whatsapp/hooks/useUpdateTemplateConfig';
import { useUpdateTemplateEnabled } from '@frontend/features/whatsapp/hooks/useUpdateTemplateEnabled';

type WhatsappTemplatesSectionProps = {
  agencyId: number;
};

const getMutationErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
};

export const WhatsappTemplatesSection = ({ agencyId }: WhatsappTemplatesSectionProps) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const { data, isLoading } = useGetTemplates(agencyId);
  const updateTemplateEnabled = useUpdateTemplateEnabled(agencyId);
  const updateTemplateConfig = useUpdateTemplateConfig(agencyId);
  const [searchText, setSearchText] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const templates = data?.templates || [];
  const selectedTemplate = useMemo<WhatsappTemplateWithEnabled | undefined>(
    () => templates.find((template) => template.id === selectedTemplateId),
    [selectedTemplateId, templates],
  );
  const filteredTemplates = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return templates;
    return templates.filter((template) => {
      const name = template.elementName || '';
      const id = template.id || '';
      return `${name} ${id}`.toLowerCase().includes(searchValue);
    });
  }, [templates, searchText]);

  const handleCloseDialog = () => {
    if (updateTemplateConfig.isLoading) return;
    setSelectedTemplateId(null);
  };

  const handleSaveTemplateConfig = async (values: {
    placeholderMappings: string[];
  }) => {
    if (!selectedTemplateId) return;

    try {
      await updateTemplateConfig.mutateAsync({
        templateId: selectedTemplateId,
        placeholderMappings: values.placeholderMappings,
      });

      addToast({
        message: t('whatsapp.settings.templates.messages.updatedSuccess'),
        color: 'success',
      });
      handleCloseDialog();
    } catch (error) {
      addToast({
        message: getMutationErrorMessage(
          error,
          t('whatsapp.settings.templates.messages.saveError'),
        ),
        color: 'error',
      });
    }
  };

  return (
    <Box maxWidth={900} display="flex" flexDirection="column" gap={2}>
      <Typography variant="h6">
        {t('whatsapp.settings.templates.title')}
      </Typography>
      <TextField
        label={t('whatsapp.settings.templates.searchLabel')}
        placeholder={t('whatsapp.settings.templates.searchPlaceholder')}
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
        fullWidth
        size="small"
      />
      {isLoading ? (
        <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 2 }} />
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('whatsapp.settings.templates.table.id')}
                </TableCell>
                <TableCell>
                  {t('whatsapp.settings.templates.table.name')}
                </TableCell>
                <TableCell align="center">
                  {t('whatsapp.settings.templates.table.enabled')}
                </TableCell>
                <TableCell align="right">
                  {t('whatsapp.settings.templates.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.id}</TableCell>
                  <TableCell>{template.elementName || template.id}</TableCell>
                  <TableCell align="center">
                    <Checkbox
                      checked={template.enabled !== false}
                      onChange={(event) =>
                        updateTemplateEnabled.mutate({
                          templateId: template.id,
                          enabled: event.target.checked,
                        })
                      }
                      disabled={updateTemplateEnabled.isLoading}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => setSelectedTemplateId(template.id)}
                      disabled={updateTemplateConfig.isLoading}
                      aria-label={t('whatsapp.settings.templates.actions.edit')}
                    >
                      <Edit color="primary" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {t('whatsapp.settings.templates.empty')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <WhatsappTemplateConfigDialog
        open={Boolean(selectedTemplateId && selectedTemplate)}
        template={selectedTemplate}
        onClose={handleCloseDialog}
        onSubmit={handleSaveTemplateConfig}
        isSubmitting={updateTemplateConfig.isLoading}
      />
    </Box>
  );
};
